import { useState } from "react";
import { common_utils } from "../../../../../../common/common_utils";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { NewPageBlurb } from "../../../guild_settings/new_page_blurb";
import { trpc } from "../../../utils/trpc";
import { DisableableLink } from "../base_components/disableable_link";
import { Disabler } from "../disabler";
import { ErrorNote } from "../error_note";
import { useServerId } from "../hooks/use_server_id";
import { Note } from "../note";
import { GuildLogChannelSettingsPage } from "./guild_log_channel_settings_page";
import { FilterMenu } from "./guild_log_filter";
import { GuildLogItem } from "./guild_log_item";
import { GuildLogList } from "./guild_log_list";

export function GuildLogsPage(props: {
	is_premium: boolean;
	renderOneOffPage: Function;
}) {
	const server_id = useServerId();

	// Have to cast this to any because useInfiniteQuery only appears if we have strict mode enabled
	const query = trpc.guild_logs.canView.useQuery({
		guild_id: server_id,
	});

	if (!query.isSuccess) {
		return <>{DashboardUtils.RenderLoadingThrobber()}</>;
	}

	if (query.data === false) {
		return (
			<ErrorNote
				error={"You do not have permission to view this server's logs"}
			/>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{!props.is_premium && (
				<Note>
					Non-premium servers can preview logs up to one day in the
					past. Get{" "}
					<DisableableLink to={common_utils.getPremiumPageURL()}>
						Premium
					</DisableableLink>{" "}
					to view unlimited logs.
				</Note>
			)}
			<div className="server-settings-form">
				<NewPageBlurb
					title="Send Logs To Channel"
					description="Send logs to a channel"
					onClick={() =>
						props.renderOneOffPage(
							<GuildLogChannelSettingsPage
								is_premium={props.is_premium}
							/>
						)
					}
					is_new
				/>
			</div>
			<GuildLogsView />
		</div>
	);
}

export function GuildLogsView() {
	const [filter, setFilter] = useState([]);
	const server_id = useServerId();
	const [fetch_date] = useState(new Date());

	// Have to cast this to any because useInfiniteQuery only appears if we have strict mode enabled
	const logs_query = (trpc.guild_logs.get as any).useInfiniteQuery(
		{
			guild_id: server_id,
			from_before_ms: fetch_date.getTime(),
			filter,
		},
		{
			getNextPageParam: (lastPage) => lastPage.next_page,
			refetchOnWindowFocus: false,
			cacheTime: Infinity,
		}
	);

	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-col gap-1">
				<Disabler disabled={logs_query.isFetching}>
					<FilterMenu filter={filter} setFilter={setFilter} />
				</Disabler>
				<GuildLogList
					key="LogsList"
					logs_query={logs_query}
					logItemCreator={(data) => <GuildLogItem log={data} />}
				/>
			</div>
		</div>
	);
}

export function DetailsPane(props: {
	log: RouterInferenceHelpers["guild_logs"]["get"]["output"]["logs"][0];
}) {
	const data = props.log.data;

	const items = Object.keys(data).map((key) => {
		const value = data[key];
		const formattedValue =
			typeof value === "object" && value !== null
				? JSON.stringify(value, null, 2)
				: JSON.stringify(value);

		return (
			<div key={key}>
				{key}: {formattedValue}
			</div>
		);
	});

	const content = (
		<div className="flex flex-col border-2 border-t-0 border-base-light bg-base-dark text-sm">
			<div className="px-4 py-2">
				<div>
					<div key={"created_at"}>
						date:{" "}
						{props.log.created_at instanceof Date
							? props.log.created_at.toString()
							: props.log.created_at ?? "unknown"}
					</div>
					{props.log.userId && (
						<div key={"user_id"}>user_id: {props.log.userId}</div>
					)}
					{props.log.channel_id && (
						<div key={"channel_id"}>
							channel_id: {props.log.channel_id}
						</div>
					)}
					{items}
				</div>
			</div>
		</div>
	);
	// @ts-ignore - React 18 type strictness workaround
	return content;
}
